/**
 * versions — an assistant turn's answer history.
 *
 * A turn can be re-run: as-is, or with an edited question. Each run is a
 * VERSION of the same message, so the transcript keeps its shape (one
 * question, one answer, in place) and the answer gains a v1 / v2 / v3 toggle
 * rather than the conversation growing a near-duplicate exchange.
 *
 * The backend always reports at least one version — a turn that has never
 * been re-run has a single SYNTHETIC one — so `version_count > 1` is the only
 * honest test for "is there anything to toggle between".
 */

export interface MessageVersion {
  uuid: string;
  /** 1-based, v1 first. */
  number: number;
  /** How this version came about, as the backend describes it. */
  origin: string;
  status: string;
  createdAt: Date | null;
  /** The question this version answered — what the edit affordance seeds. */
  query: string;
  /** For a refine: what the user asked to be improved. May be absent. */
  instruction: string;
  /** For a refine: the version it was produced from. */
  refineFrom: number | null;
  /** THIS user's rating of THIS version. */
  feedback: 'up' | 'down' | null;
}

export interface VersionInfo {
  versions: MessageVersion[];
  /** The version the backend considers current — what we show by default. */
  currentVersion: number;
  versionCount: number;
}

const str = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

function toVersion(raw: any, index: number): MessageVersion | null {
  if (!raw) return null;
  if (typeof raw === 'number') {
    return {
      uuid: '', number: raw, origin: '', status: '', createdAt: null,
      query: '', instruction: '', refineFrom: null, feedback: null,
    };
  }
  if (typeof raw !== 'object') return null;

  const number = [raw.version_number, raw.number, raw.version]
    .find((v) => typeof v === 'number' && v > 0);
  const created = str(raw.created_at) || str(raw.createdAt);

  return {
    uuid: str(raw.version_uuid) || str(raw.uuid) || str(raw.id),
    // Position is the last resort: v1 first is the contract's own ordering.
    number: typeof number === 'number' ? number : index + 1,
    origin: str(raw.origin),
    status: str(raw.status),
    createdAt: created ? new Date(created) : null,
    query: str(raw.query) || str(raw.question) || str(raw.query_text),
    instruction: str(raw.instruction),
    refineFrom:
      typeof raw.refine_from_version === 'number' ? raw.refine_from_version
        : typeof raw.refineFromVersion === 'number' ? raw.refineFromVersion
        : null,
    feedback: raw.feedback === 'up' || raw.feedback === 'down' ? raw.feedback : null,
  };
}

/**
 * The version block on a message row.
 *
 * Absent or malformed means "no version history to show" — never an error,
 * and never a fabricated second version.
 */
export function versionsFromMessage(item: any): VersionInfo {
  const raw = Array.isArray(item?.versions) ? item.versions : [];
  const versions = raw
    .map(toVersion)
    .filter((v: MessageVersion | null): v is MessageVersion => v !== null)
    .sort((a: MessageVersion, b: MessageVersion) => a.number - b.number);

  const declared = [item?.version_count, item?.versionCount]
    .find((v) => typeof v === 'number' && v >= 0);
  // The list is the truth when it disagrees with the count: a toggle offering
  // versions that are not there would 404 on click.
  const versionCount = versions.length || (typeof declared === 'number' ? declared : 0);

  const current = [item?.current_version, item?.currentVersion]
    .find((v) => typeof v === 'number' && v > 0);

  return {
    versions,
    // Default to the latest: a rerun makes the new version current, and
    // landing on v1 after re-running would look like the rerun did nothing.
    currentVersion:
      typeof current === 'number'
        ? current
        : versions.length
        ? versions[versions.length - 1].number
        : 1,
    versionCount,
  };
}

/** Show the toggle only when there is genuinely more than one answer. */
export function hasVersionHistory(info: VersionInfo | undefined): boolean {
  return !!info && info.versionCount > 1 && info.versions.length > 1;
}

/** The version to display when nothing has been chosen. */
export function defaultVersion(info: VersionInfo | undefined): number {
  if (!info) return 1;
  const known = info.versions.some((v) => v.number === info.currentVersion);
  if (known) return info.currentVersion;
  return info.versions.length ? info.versions[info.versions.length - 1].number : 1;
}

/** The question to seed an edit with — this version's, else the newest one's. */
export function versionQuery(info: VersionInfo | undefined, number?: number): string {
  if (!info) return '';
  const exact = info.versions.find((v) => v.number === number);
  if (exact?.query) return exact.query;
  return [...info.versions].reverse().find((v) => v.query)?.query || '';
}

/** Is any version of this turn still being produced? */
export function isVersionProcessing(info: VersionInfo | undefined): boolean {
  return !!info?.versions.some((v) => {
    const status = v.status.toLowerCase();
    return status === 'processing' || status === 'pending' || status === 'queued';
  });
}

/**
 * What a version segment says it is.
 *
 * PLAIN NUMBERS, not "v1". The control is labelled "Versions" beside it, so
 * the number needs no prefix — and "v1 v2" read as something technical rather
 * than as "the first and second answers".
 *
 * A refine is the one origin whose provenance matters at a glance: it is
 * derived from a specific earlier version, and the number alone loses that.
 */
export function versionLabel(version: MessageVersion): string {
  if (version.origin === 'refine' && version.refineFrom) {
    return `Refined from ${version.refineFrom}`;
  }
  return String(version.number);
}

/** What the control itself is called, next to the numbers. */
export const VERSIONS_LABEL = 'Versions';

/** The tooltip for a chip — the instruction, when the user gave one. */
export function versionHint(version: MessageVersion): string {
  if (version.instruction) return version.instruction;
  return version.origin || '';
}

/**
 * Fold a newly accepted version into a turn's history, LIVE.
 *
 * `version_count` comes from `GET /messages`, which is not refetched after a
 * rerun — so the toggle stayed at one version, and only appeared after a
 * reload. The 202 already tells us the new version's number and origin, which
 * is everything the toggle needs, so the history is reconciled here rather
 * than waiting for the next page load.
 *
 * Idempotent: re-accepting the same number replaces that entry instead of
 * appending a duplicate, so a retried or echoed acceptance cannot produce two
 * "v2" chips.
 */
export function withNewVersion(
  info: VersionInfo | undefined,
  accepted: {
    version_uuid?: string;
    version_number: number;
    origin?: string;
    status?: string;
    instruction?: string | null;
    refine_from_version?: number | null;
  },
  /** The question this version answers, when the caller knows it. */
  query?: string
): VersionInfo {
  const existing = info?.versions || [];
  const previous = existing.find((v) => v.number === accepted.version_number);

  const entry: MessageVersion = {
    uuid: str(accepted.version_uuid) || previous?.uuid || '',
    number: accepted.version_number,
    origin: str(accepted.origin) || previous?.origin || '',
    status: str(accepted.status) || 'processing',
    createdAt: previous?.createdAt ?? null,
    // A rerun with no edit answers the same question as before.
    query: str(query) || previous?.query || versionQuery(info),
    instruction: str(accepted.instruction) || previous?.instruction || '',
    refineFrom:
      typeof accepted.refine_from_version === 'number'
        ? accepted.refine_from_version
        : previous?.refineFrom ?? null,
    // A fresh version has not been rated; a re-accepted one keeps its rating.
    feedback: previous?.feedback ?? null,
  };

  const kept = existing.filter((v) => v.number !== entry.number);

  /**
   * Fill any earlier version the turn never told us about.
   *
   * Not the same as inventing history from a count: the server accepting a
   * rerun AS v3 is proof that v1 and v2 exist, and `selectVersion` fetches
   * by number, so these placeholders resolve to real content when clicked.
   * Without this, a turn whose row carried no version block would gain a
   * lone "v2" and still show no toggle — the reported bug.
   */
  const missing: MessageVersion[] = [];
  for (let n = 1; n < entry.number; n += 1) {
    if (kept.some((v) => v.number === n)) continue;
    missing.push({
      uuid: '', number: n, origin: '', status: 'completed', createdAt: null,
      query: '', instruction: '', refineFrom: null, feedback: null,
    });
  }

  const versions = [...kept, ...missing, entry].sort((a, b) => a.number - b.number);

  return {
    versions,
    // The new version is current the moment it is accepted.
    currentVersion: entry.number,
    versionCount: versions.length,
  };
}

/**
 * The rating of one version.
 *
 * Feedback is per VERSION, not per message: v1 and v2 are different answers,
 * and a thumb given to one says nothing about the other. `fallback` is the
 * message-level rating, still read while the backend finishes moving the
 * field onto the version.
 */
export function versionFeedback(
  info: VersionInfo | undefined,
  number: number | undefined,
  fallback: 'up' | 'down' | null = null
): 'up' | 'down' | null {
  const version = info?.versions.find((v) => v.number === number);
  if (!version) return fallback;
  return version.feedback ?? (number === info?.currentVersion ? fallback : null);
}

/** Record a rating against one version. */
export function withVersionFeedback(
  info: VersionInfo | undefined,
  number: number,
  rating: 'up' | 'down' | null
): VersionInfo | undefined {
  if (!info) return info;
  if (!info.versions.some((v) => v.number === number)) return info;
  return {
    ...info,
    versions: info.versions.map((v) => (v.number === number ? { ...v, feedback: rating } : v)),
  };
}

/** Mark a version finished, once its answer has landed. */
export function markVersionStatus(
  info: VersionInfo | undefined,
  number: number,
  status: string
): VersionInfo | undefined {
  if (!info) return info;
  if (!info.versions.some((v) => v.number === number)) return info;
  return {
    ...info,
    versions: info.versions.map((v) => (v.number === number ? { ...v, status } : v)),
  };
}

/* ------------------------------------------------------------------ */
/*  Rerun outcomes                                                     */
/* ------------------------------------------------------------------ */

export type RerunFailure =
  | 'not-assistant'
  | 'still-answering'
  | 'no-query'
  | 'unknown-version'
  | 'limit-reached'
  | 'unavailable'
  | 'failed';

export interface RerunAccepted {
  ok: true;
  versionUuid: string;
  versionNumber: number;
  origin: string;
}

export interface RerunRejected {
  ok: false;
  reason: RerunFailure;
}

export type RerunResult = RerunAccepted | RerunRejected;

/**
 * The HTTP status → what actually went wrong.
 *
 * Each of these is a different thing for the user to do about it, so they are
 * kept apart rather than collapsed into "something went wrong".
 */
export function rerunFailureFor(status: number | undefined): RerunFailure {
  switch (status) {
    case 400: return 'not-assistant';
    // Only refine sends a version to work from, so a 404 here is that
    // version, not the message.
    case 404: return 'unknown-version';
    case 409: return 'still-answering';
    case 422: return 'no-query';
    case 429: return 'limit-reached';
    case 503: return 'unavailable';
    default: return 'failed';
  }
}

/** English copy for a failure. Callers translate. */
export const RERUN_MESSAGES: Record<RerunFailure, string> = {
  'not-assistant': 'Only an answer can be re-run.',
  'still-answering': 'This answer is still being written. Wait for it to finish, then re-run.',
  'no-query': 'The original question could not be recovered. Edit it and run again.',
  'unknown-version': 'That version no longer exists. Pick another and try again.',
  'limit-reached': 'This answer has reached its version limit.',
  unavailable: 'The research queue is unavailable right now. Try again shortly.',
  failed: 'Could not re-run this answer.',
};
